// The host's rule editor of the lobby panel: text drafts of the rules, their validation and the patch
// that is sent. Pure functions (unit tested); the panel keeps the draft in component state.

import type { GameMode } from "../../../api/types";
import type { LobbyRules, LobbyRulesPatch } from "../../../game/lobby";
import { MODE_RULE_KEYS, PLAYERS_MAX, PLAYERS_MIN, RULE_SPECS, validateRuleText } from "../rooms/lobbySettings";
import type { RuleKey } from "../rooms/lobbySettings";

/** What the inputs hold: numbers as text (minutes for the time limit), the friendly fire switch as is. */
export interface RulesDraft {
  maxPlayers: number;
  scoreLimit: string;
  timeLimitMin: string;
  roundsToWin: string;
  friendlyFire: boolean;
}

export interface DraftErrors {
  maxPlayers?: string;
  scoreLimit?: string;
  timeLimitMin?: string;
  roundsToWin?: string;
}

function minutes(seconds: number): string {
  const m = seconds / 60;
  return Number.isInteger(m) ? String(m) : m.toFixed(1);
}

/** The draft that shows the room's current rules (no pending change). */
export function draftFromRules(rules: LobbyRules): RulesDraft {
  return {
    maxPlayers: rules.maxPlayers,
    scoreLimit: String(rules.scoreLimit),
    timeLimitMin: minutes(rules.timeLimitSec),
    roundsToWin: String(rules.roundsToWin),
    friendlyFire: rules.friendlyFire,
  };
}

/** Rule inputs that exist for a mode, in display order (Defusal has rounds only, the others score and time). */
export function visibleRules(mode: GameMode): readonly RuleKey[] {
  return MODE_RULE_KEYS[mode];
}

const DRAFT_FIELD: Record<RuleKey, "scoreLimit" | "timeLimitMin" | "roundsToWin"> = {
  scoreLimit: "scoreLimit",
  timeLimitSec: "timeLimitMin",
  roundsToWin: "roundsToWin",
};

export function draftField(key: RuleKey): "scoreLimit" | "timeLimitMin" | "roundsToWin" {
  return DRAFT_FIELD[key];
}

/** Error text per field. `players` is how many people are in the room: the server refuses fewer slots than that. */
export function validateDraft(mode: GameMode, draft: RulesDraft, players: number): DraftErrors {
  const errors: DraftErrors = {};
  if (!Number.isInteger(draft.maxPlayers) || draft.maxPlayers < PLAYERS_MIN || draft.maxPlayers > PLAYERS_MAX) {
    errors.maxPlayers = `Choose ${PLAYERS_MIN}-${PLAYERS_MAX} players`;
  } else if (draft.maxPlayers < players) {
    errors.maxPlayers = `${players} players are in the room`;
  }
  for (const key of visibleRules(mode)) {
    const field = DRAFT_FIELD[key];
    const error = validateRuleText(key, draft[field]);
    if (error !== null) errors[field] = error;
  }
  return errors;
}

export function hasErrors(errors: DraftErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Only what differs from the room's rules, in the units of the protocol (seconds). Input must be valid. */
export function draftToPatch(mode: GameMode, rules: LobbyRules, draft: RulesDraft): LobbyRulesPatch {
  const patch: LobbyRulesPatch = {};
  if (draft.maxPlayers !== rules.maxPlayers) patch.maxPlayers = draft.maxPlayers;
  if (draft.friendlyFire !== rules.friendlyFire) patch.friendlyFire = draft.friendlyFire;
  for (const key of visibleRules(mode)) {
    const value = Math.round(Number(draft[DRAFT_FIELD[key]].trim()) * RULE_SPECS[key].scale);
    if (value !== rules[key]) patch[key] = value;
  }
  return patch;
}

export function isDirty(patch: LobbyRulesPatch): boolean {
  return Object.keys(patch).length > 0;
}
